import { PropsWithChildren } from 'react';

import { CircularProgress, type IconButtonProps as MuiIconButtonProps } from '@mui/material';
import { IconButton as DsIconButton } from '@natter-co/design-system';
import { VISUAL_REFRESH } from 'common/utils';

import { mapButtonSizeToLoaderSize } from '../Button/utils/mapButtonSizeToLoaderSize';
import { LoadingIndicatorContainer, StyledIconButton } from './IconButton.styled';
import { toDsIconProps } from './toDsIconProps';

import type { IconButtonVariant, IconButtonVariantColor } from './types';

export type IconButtonProps = PropsWithChildren<
  Pick<
    MuiIconButtonProps,
    | 'aria-label'
    | 'disabled'
    | 'size'
    | 'onClick'
    | 'sx'
    | 'className'
    | 'disableRipple'
    | 'onMouseDown'
    | 'title'
    | 'type'
  > & {
    color?: IconButtonVariantColor;
    isLoading?: boolean;
    variant?: IconButtonVariant;
  }
>;

export const IconButton = ({
  children,
  color = 'primary',
  disabled,
  isLoading,
  size = 'medium',
  variant,
  disableRipple,
  ...buttonProps
}: IconButtonProps) => {
  /**
   * The visual-refresh gate lives HERE, not at the ~76 call sites - they keep this component's
   * MUI-shaped API and never mention the flag. `toDsIconProps` returns `null` for anything DS 2.0
   * cannot express, which falls through to the MUI implementation below.
   *
   * The DS button is circular rather than a rounded square, which is the point of the swap.
   */
  const dsProps = VISUAL_REFRESH ? toDsIconProps({ color, disabled, isLoading, size, variant, ...buttonProps }) : null;

  if (dsProps) {
    // Dropped because they are meaningless on a component with no Emotion and no ripple: `sx` is
    // refused by the mapper anyway, and `title` would paint a second, native tooltip on top of the
    // one `IconButtonWithTooltip` already renders. `dsProps` supplies the `aria-label`.
    const { sx: _sx, title: _title, ...rest } = buttonProps;

    return (
      <DsIconButton {...rest} {...dsProps} disabled={disabled}>
        {children}
      </DsIconButton>
    );
  }

  return (
    <StyledIconButton
      iconColor={color}
      disabled={isLoading || disabled}
      disableRipple={disableRipple ?? variant === 'text'}
      isLoading={isLoading}
      variant={variant}
      size={size}
      {...buttonProps}
    >
      {isLoading ? (
        <LoadingIndicatorContainer alignItems="center">
          <CircularProgress color="inherit" size={mapButtonSizeToLoaderSize(size)} />
        </LoadingIndicatorContainer>
      ) : (
        children
      )}
    </StyledIconButton>
  );
};
